import { updateStore } from 'core_main/store';
import Pubnub from 'pubnub';
import {
  PUBNUB_ACCESS_DENIED_CATEGORY,
  PUBNUB_BAD_REQUEST_CATEGORY,
  PUBNUB_NETWORK_DOWN_CATEGORY,
  PUBNUB_NETWORK_RECONNECTED_CATEGORY,
  PUBNUB_NETWORK_UP_CATEGORY,
} from '../constants';
import {
  isAuthTokenExpired,
  setupGrantTokenRefresh,
  teardownGrantTokenRefresh,
} from '../helpers/grantToken';
import { logHealthCheckError } from '../helpers/logError';

// Type for the health check function to avoid circular dependency
type HealthCheckFunction = () => Promise<void>;

// Module-scoped variable to store the injected health check function
let injectedHealthCheck: HealthCheckFunction | null = null;

/** Coalesce concurrent AccessDenied grant refreshes from status events. */
let accessDeniedRecoveryInFlight = false;

/**
 * Sets the health check function to be called after PubNub status events.
 * This uses dependency injection to avoid circular dependencies.
 * Called from index.ts during initialization.
 */
export function setHealthCheckFunction(healthCheck: HealthCheckFunction) {
  injectedHealthCheck = healthCheck;
}

export function createPubnubNetworkStatusEventHandler(outboundChatEnabled) {
  // this listener returns a promise for ease of testing but pubnub does not wait for the promise returned to resolve
  return (statusEvent: Pubnub.StatusEvent) => {
    const { category } = statusEvent;
    if (category === PUBNUB_NETWORK_DOWN_CATEGORY) {
      teardownGrantTokenRefresh();
    } else if (category === PUBNUB_BAD_REQUEST_CATEGORY) {
      // Empty channel-group recovery is owned by the conversation subscribe ack
      // path (initializePubNub onConnectedAck). Do not schedule it from this
      // shared handler — meta-client BadRequests would burn the conversation
      // retry budget and false-exhaust (Baz / PROD-2159).
      return Promise.resolve();
    } else if (category === PUBNUB_ACCESS_DENIED_CATEGORY) {
      // Token may still be within TTL but missing grants for newly provisioned
      // post-merge channels. Always refresh PAM and reconnect — not only when
      // the local expiry clock says the token is expired. Coalesce bursts.
      if (accessDeniedRecoveryInFlight) {
        return Promise.resolve();
      }
      accessDeniedRecoveryInFlight = true;
      return setupGrantTokenRefresh(outboundChatEnabled)
        .then(() => {
          if (injectedHealthCheck) {
            return injectedHealthCheck();
          }
          return undefined;
        })
        .catch(() => {
          updateStore('isPubNubConnected', false);
        })
        .finally(() => {
          accessDeniedRecoveryInFlight = false;
        });
    } else if (
      category === PUBNUB_NETWORK_RECONNECTED_CATEGORY ||
      category === PUBNUB_NETWORK_UP_CATEGORY
    ) {
      if (isAuthTokenExpired()) {
        // Token refresh already calls health check via refreshGrantToken() -> injectedHealthCheck()
        // No need to call it again here to avoid duplicate health checks
        return setupGrantTokenRefresh(outboundChatEnabled).catch(() => {
          updateStore('isPubNubConnected', false);
        });
      }
      // PubNub Status trigger: Check and reconnect after network reconnected/up events
      // Only call if token was NOT expired (already handled above)
      // Non-blocking call (fire-and-forget) to avoid delaying status event handler
      // Uses dependency injection to avoid circular dependency
      if (injectedHealthCheck) {
        injectedHealthCheck().catch((error) => {
          logHealthCheckError(
            error,
            'Health check failed on network reconnected/up',
          );
        });
      }
    }
    return Promise.resolve();
  };
}
